import { RADIO_QUEUE_KINDS } from './hitLabRadioTracks';

export const RADIO_LIVE_CAPSULE_READY_TEXT =
  'Radio Rotation is getting ready.';

const cleanText = (value, max = 256) =>
  String(value || '').trim().slice(0, max);

const normalizeIndex = (index, length) => {
  if (!length) return 0;
  const next = Number(index);
  if (!Number.isFinite(next)) return 0;
  return ((Math.trunc(next) % length) + length) % length;
};

const getTrackTitle = (item) =>
  cleanText(item?.songTitle || item?.title, 160);

const getTrackArtistName = (item) => cleanText(item?.artistName, 120);

const toIsoString = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

const normalizeSignalTrack = (item = {}, dateField) => {
  const trackId = cleanText(item?.trackId || item?.id, 120);
  const title = cleanText(item?.title || item?.songTitle, 160);
  const artistName = cleanText(item?.artistName, 120);
  const timestamp = toIsoString(item?.[dateField]);
  if (!trackId || !title || !artistName || !timestamp) return null;
  return {
    trackId,
    title,
    artistName,
    [dateField]: timestamp,
  };
};

const isFreshSignal = (signal = {}, dateField, nowMs, ttlMs) => {
  const timestamp = new Date(signal?.[dateField] || '').getTime();
  if (!Number.isFinite(timestamp)) return false;
  return timestamp <= nowMs && nowMs - timestamp <= ttlMs;
};

const normalizeCtaUrl = (value) => {
  const raw = cleanText(value, 240);
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return '';
  if (/[\u0000-\u001f\u007f\\]/.test(raw)) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return '';
  return raw;
};

const normalizeProgrammingMessage = (item = null) => {
  if (!item || typeof item !== 'object') return null;
  const message = cleanText(item.message, 120);
  if (!message) return null;
  return {
    eyebrow: cleanText(item.eyebrow, 32),
    message,
    ctaLabel: cleanText(item.ctaLabel, 32),
    ctaUrl: normalizeCtaUrl(item.ctaUrl),
    activeUntil: toIsoString(item.activeUntil),
  };
};

const isLiveCapsuleTrack = (item) =>
  item?.kind === RADIO_QUEUE_KINDS.TRACK &&
  Boolean(getTrackTitle(item)) &&
  Boolean(getTrackArtistName(item)) &&
  Boolean(cleanText(item?.audioUrl, 2048));

export const getNextTrackForLiveCapsule = ({
  queue = [],
  currentIndex = 0,
} = {}) => {
  const safeQueue = Array.isArray(queue) ? queue : [];
  const queueLength = safeQueue.length;
  if (queueLength <= 1) return null;

  const safeCurrentIndex = normalizeIndex(currentIndex, queueLength);
  for (let offset = 1; offset < queueLength; offset += 1) {
    const candidateIndex = (safeCurrentIndex + offset) % queueLength;
    const candidate = safeQueue[candidateIndex];
    if (isLiveCapsuleTrack(candidate)) {
      return {
        trackId: cleanText(candidate.trackId || candidate.id, 120),
        title: getTrackTitle(candidate),
        artistName: getTrackArtistName(candidate),
      };
    }
  }

  return null;
};

export const normalizeRadioLiveCapsuleSignals = (response = null) => {
  const data = response?.data?.data || response?.data || response || {};
  const rotationDepth = Number(data.rotationDepth || 0);
  const newInRotation = (Array.isArray(data.newInRotation)
    ? data.newInRotation
    : [])
    .map((item) => normalizeSignalTrack(item, 'activatedAt'))
    .filter(Boolean)
    .slice(0, 3);
  const feedbackSignals = (Array.isArray(data.feedbackSignals)
    ? data.feedbackSignals
    : [])
    .map((item) => normalizeSignalTrack(item, 'receivedAt'))
    .filter(Boolean)
    .slice(0, 3);

  return {
    generatedAt: toIsoString(data.generatedAt),
    rotationDepth:
      Number.isFinite(rotationDepth) && rotationDepth > 0
        ? Math.floor(rotationDepth)
        : 0,
    newInRotation,
    feedbackSignals,
    programmingMessage: normalizeProgrammingMessage(data.programmingMessage),
  };
};

export const buildRadioLiveCapsuleMessage = ({
  currentCapsule = null,
  queue = [],
  currentIndex = 0,
  isPlaying = false,
  signals = null,
  transientFeedbackSignal = null,
  nowMs = Date.now(),
  feedbackSignalTtlMs = 4000,
} = {}) => {
  if (!currentCapsule) {
    return {
      brand: 'Joe Galaxy Radio',
      status: 'Radio Rotation',
      title: RADIO_LIVE_CAPSULE_READY_TEXT,
      artistName: '',
      liveLabel: '',
      isReady: false,
      isPlaying: false,
      secondaryLabel: '',
      secondaryTitle: '',
      secondaryArtistName: '',
      upNext: null,
    };
  }

  const isBumper = currentCapsule.kind === RADIO_QUEUE_KINDS.BUMPER;
  const upNext = getNextTrackForLiveCapsule({ queue, currentIndex });
  const safeSignals = normalizeRadioLiveCapsuleSignals(signals);
  const programmingMessage = safeSignals.programmingMessage || null;
  const transientSignal = normalizeSignalTrack(
    transientFeedbackSignal,
    'receivedAt',
  );
  const feedbackSignal =
    transientSignal ||
    safeSignals.feedbackSignals.find((signal) =>
      isFreshSignal(signal, 'receivedAt', nowMs, feedbackSignalTtlMs),
    ) ||
    null;
  const newInRotationSignal = safeSignals.newInRotation[0] || null;

  let secondaryLabel = 'In Rotation';
  let secondaryTitle = '';
  let secondaryArtistName = '';
  if (programmingMessage) {
    secondaryLabel = programmingMessage.eyebrow || 'Radio Message';
    secondaryTitle = programmingMessage.message;
  } else if (feedbackSignal) {
    secondaryLabel = 'Feedback Landed';
    secondaryTitle = feedbackSignal.title;
    secondaryArtistName = feedbackSignal.artistName;
  } else if (newInRotationSignal) {
    secondaryLabel = 'New In Rotation';
    secondaryTitle = newInRotationSignal.title;
    secondaryArtistName = newInRotationSignal.artistName;
  } else if (upNext) {
    secondaryLabel = 'Up Next';
    secondaryTitle = upNext.title;
    secondaryArtistName = upNext.artistName;
  } else if (safeSignals.rotationDepth > 0) {
    secondaryLabel = `${safeSignals.rotationDepth} ${
      safeSignals.rotationDepth === 1 ? 'Record' : 'Records'
    } In Rotation`;
  }

  return {
    brand: 'Joe Galaxy Radio',
    status: isBumper ? 'In Rotation' : 'Now Playing',
    title: currentCapsule.title || 'Radio Rotation',
    artistName: isBumper ? 'Radio Rotation' : currentCapsule.artistName || '',
    liveLabel: 'Live',
    isReady: Boolean(currentCapsule.canPlay),
    isPlaying: Boolean(isPlaying),
    secondaryLabel,
    secondaryTitle,
    secondaryArtistName,
    upNext,
    programmingMessage,
    feedbackSignal,
    newInRotationSignal,
    rotationDepth: safeSignals.rotationDepth,
  };
};
